<template>
  <div class="d-flex flex-column justify-space-between fill-height">
    <!-- Header showing current type -->
    <div class="flex-shrink-0 mb-2">
      <div class="text-subtitle-2 grey--text">Current Type</div>
      <div class="text-h6">{{ currentTypeName }}</div>
    </div>

    <!-- Search field -->
    <div class="flex-shrink-0">
      <v-text-field
        v-model="searchText"
        solo
        clearable
        label="Filter types..."
        messages="Start typing to filter"
        prepend-inner-icon="mdi-magnify"
        @input="onInput"
        ref="searchField"
      ></v-text-field>
    </div>

    <!-- Tree view or no options message -->
    <div class="flex-grow-1 scrollable-treeview">
      <template v-if="hasCompatibleTypes">
        <v-treeview
          activatable
          @update:active="onActive"
          :search="debouncedSearchText"
          :items="tree"
          :open.sync="open"
          item-disabled="_noCascade"
        >
          <template v-slot:label="{ item }">
            <span
              :class="{
                'grey--text': item.disabled,
                'font-weight-bold': item.isCurrent,
              }"
            >
              {{ item.name }}
            </span>
          </template>
        </v-treeview>
      </template>

      <template v-else>
        <v-alert type="info" outlined class="mt-4">
          <div class="text-subtitle-1 mb-2">
            No compatible type changes available.
          </div>
          <div class="text-body-2">
            This individual has relationships that constrain its type:
          </div>
          <div v-if="outgoingCount > 0" class="mt-2">
            <strong>Outgoing:</strong> {{ outgoingCount }} relationship(s)
          </div>
          <div v-if="incomingCount > 0" class="mt-1">
            <strong>Incoming:</strong> {{ incomingCount }} relationship(s)
          </div>
          <div class="mt-2 text-body-2">
            {{ currentTypeName }} is the only compatible type in this hierarchy.
            To change type, you would need to remove some relationships first.
          </div>
        </v-alert>
      </template>
    </div>

    <div v-if="hasCompatibleTypes" class="flex-shrink-0">
      <div class="text-caption grey--text mt-2">
        Only types compatible with current relationships are shown.
      </div>
    </div>

    <!-- Action buttons -->
    <div class="flex-shrink-0 mt-4">
      <v-btn
        v-if="hasCompatibleTypes"
        text
        color="secondary"
        :disabled="!selectedType"
        @click="onChangeType"
      >
        {{ changeButtonText }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import debounce from 'debounce'
import {
  getCompatibleTypesInHierarchy,
  buildCompatibleTypeTree,
} from '@/wob-ontology-services/type-compatibility'

export default {
  props: {
    individual: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    tree: [],
    open: [],
    selectedType: null,
    searchText: '',
    debouncedSearchText: '',
    compatibilityResult: null,
  }),
  computed: {
    currentTypeName() {
      return this.individual?.type?.short || 'Unknown'
    },
    hasCompatibleTypes() {
      return this.compatibilityResult?.hasCompatibleTypes || false
    },
    outgoingCount() {
      return this.compatibilityResult?.outgoingEdges?.length || 0
    },
    incomingCount() {
      return this.compatibilityResult?.incomingEdges?.length || 0
    },
    changeButtonText() {
      if (!this.selectedType) return 'Select a type'
      return `Change to ${this.selectedType.short}`
    },
  },
  mounted() {
    this.analyzeCompatibility()
  },
  watch: {
    individual: {
      handler() {
        this.analyzeCompatibility()
      },
      deep: true,
    },
  },
  methods: {
    analyzeCompatibility() {
      // Analyze which types are compatible
      this.compatibilityResult = getCompatibleTypesInHierarchy(
        this.individual,
        this.$wob,
        this.$wobo
      )

      // Build tree structure
      if (this.compatibilityResult.hasCompatibleTypes) {
        this.tree = buildCompatibleTypeTree(
          this.compatibilityResult,
          this.$wobo
        )

        // Open all tree nodes
        this.open = this.expandAll(this.tree)
      }
    },

    // Recursively get all expandable node IDs
    expandAll(nodes) {
      const expandable = []
      const traverse = (items) => {
        items.forEach((item) => {
          if (item.children && item.children.length > 0) {
            expandable.push(item.id)
            traverse(item.children)
          }
        })
      }
      traverse(nodes)
      return expandable
    },

    onActive(active) {
      const [activatedId] = active

      if (!activatedId) {
        this.selectedType = null
        return
      }

      // Find the activated type in compatible types
      const compatibleType = this.compatibilityResult.compatibleTypes.find(
        (ct) => ct.value === activatedId
      )

      this.selectedType = compatibleType || null
    },

    onInput: debounce(function () {
      this.debouncedSearchText = this.searchText
    }, 300),

    onChangeType() {
      if (!this.selectedType) return
      this.$emit('change-type', this.selectedType)
    },
  },
}
</script>

<style scoped>
.fill-height {
  height: 100%;
}

.scrollable-treeview {
  overflow-y: auto;
}
</style>
